import React, { useEffect, useState } from "react";
import { Form, Formik } from "formik";
import { Button, Card, Col, Row, Stack } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { FaUserEdit } from "react-icons/fa";
import InputText from "../../../components/common/InputText";
import {
  updateVillageAPI,
  getVillageById,
} from "../../../services/villageService";
import { toast } from "react-toastify";
import { updateVillageSchema } from "../../../validations/ValidationSchema";
import { useSelector } from "react-redux";
import NotAuthorized from "../../../components/common/NotAuthorized";

function Edit() {
  // MANAGING PERMISSIONS HERE

  const roleDetails = useSelector(
    (state) => state?.rolesData?.masterPermissionResponseDTOList
  );

  const [isAuthorized, setIsAuthorized] = useState(false);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (roleDetails) {
      const permissions = roleDetails.find((detail) => {
        return detail.childPermissions.find(
          (childPerm) =>
            childPerm?.key === "village_edit" && childPerm?.status === true
        );
      });
      if (permissions) {
        setIsAuthorized(true);
      } else {
        setIsAuthorized(false);
      }
    } else {
      setIsAuthorized(true);
    }
  }, [roleDetails]);

  const navigate = useNavigate();

  const [initialValues, setInitialValues] = useState({
    directorate_en: "",
    directorate_ar: "",
    governorate_en: "",
    governorate_ar: "",
    village_en: "",
    village_ar: "",
    village_code: "",
  });

  // GET ID FROM URL PARAMS
  const { id } = useParams();

  // FORM SUBMIT FOR EDIT VILLAGE DETAILS
  const handleSubmit = (values, actions) => {
    setLoading(true);
    const formData = {
      id: id,
      villageCode: values?.village_code,
      title: { en: values?.village_en, ar: values?.village_ar },
    };
    // CALLING API FOR UPDATE VILLAGE DETAILS
    updateVillageAPI(formData)
      .then((response) => {
        navigate(-1);
        toast.success(response?.data?.message);
      })
      .catch((error) => {
        if (error?.status == 500) {
          toast.error(
            "Unauthorized or server issue. Please refresh or try later. "
          );
        } else {
          toast.error(
            error?.error_description
              ? error?.error_description
              : error?.details
                ? error?.details
                : error?.message
          );
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // FETCH VILLAGE DETAILS FROM API
  const getVillageDetails = () => {
    // GETTING DATA FROM API
    getVillageById(id)
      .then((response) => {
        setInitialValues({
          village_code: response?.villageCode,
          village_en: response?.title?.en,
          village_ar: response?.title?.ar,
          directorate_en: response?.directorate?.en,
          directorate_ar: response?.directorate?.ar,
          governorate_en: response?.governorate?.en,
          governorate_ar: response?.governorate?.ar,
        });
      })
      .catch((error) => {
        if (error?.status == 500) {
          toast.error(
            "Unauthorized or server issue. Please refresh or try later. "
          );
        } else {
          toast.error(
            error?.error_description
              ? error?.error_description
              : error?.details
                ? error?.details
                : error?.message
          );
        }
      });
  };

  // IF USER IS AUTHORIZED THEN ONLY GET VILLAGE DETAILS
  useEffect(() => {
    if (isAuthorized) {
      getVillageDetails();
    } else {
      setLoading(false);
    }
  }, [isAuthorized]);

  return (
    <div className="d-flex flex-column pageContainer p-3 h-100 overflow-auto">
      <div className="flex-grow-1 pageContent position-relative pt-4">
        {!isAuthorized ? (
          <NotAuthorized />
        ) : (
          <Card className="h-100 bg-white shadow border-0 theme-card-cover">
            <div className="theme-card-header px-1">
              <Stack
                direction="horizontal"
                gap={2}
                className="flex-wrap justify-content-between px-3 pt-3 pb-2 align-items-start"
              >
                <h5 className="mb-0 position-relative fw-medium">
                  <div className="align-items-center bg-primary d-inline-flex h-60 justify-content-center  position-absolute rounded start-0 text-white theme-icon-box w-60 z-1">
                    <span className="theme-icon-box-inner">
                      <FaUserEdit size={36} />
                    </span>
                  </div>
                  Edit Village
                </h5>
              </Stack>
            </div>
            <div className="flex-grow-1 d-flex flex-column px-1">
              <div className="p-3 d-flex flex-column h-100">
                <Formik
                  enableReinitialize={true}
                  validationSchema={updateVillageSchema}
                  initialValues={initialValues}
                  onSubmit={(values, actions) => {
                    setLoading(true);
                    actions.setSubmitting(false);
                    handleSubmit(values, actions);
                  }}
                >
                  {({
                    handleSubmit,
                    handleChange,
                    handleBlur,
                    values,
                    setFieldValue,
                    touched,
                    isValid,
                    errors,
                  }) => (
                    <Form className="d-flex flex-column flex-grow-1 theme-from">
                      <Row className="gx-xl-5">
                        <Col sm={6} md={6} lg={6} className="mb-4 pb-1">
                          <InputText
                            controlId="village_en"
                            label="Village english"
                            value={values?.village_en}
                            name="village_en"
                            maxLength="70"
                            errorsField={errors?.village_en}
                            touched={touched?.village_en}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            className={"w-100 form-control"}
                            variant="outlined"
                          />
                        </Col>
                        <Col sm={6} md={6} lg={6} className="mb-4 pb-1">
                          <div>
                            <InputText
                              dir="rtl"
                              controlId="village_ar"
                              label="Village arabic"
                              value={values?.village_ar}
                              name="village_ar"
                              maxLength="70"
                              errorsField={errors?.village_ar}
                              touched={touched?.village_ar}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                              className={"w-100 form-control"}
                              variant="outlined"
                            />
                          </div>
                        </Col>
                        <Col sm={6} md={6} lg={6} className="mb-4 pb-1">
                          <InputText
                            controlId="directorate_en"
                            label="Directorate english"
                            value={values?.directorate_en}
                            name="directorate_en"
                            maxLength="70"
                            disabled={true}
                            errorsField={errors?.directorate_en}
                            touched={touched?.directorate_en}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            className={"w-100 form-control"}
                            variant="outlined"
                          />
                        </Col>
                        <Col sm={6} md={6} lg={6} className="mb-4 pb-1">
                          <div>
                            <InputText
                              dir="rtl"
                              controlId="directorate_ar"
                              label="Directorate arabic"
                              value={values?.directorate_ar}
                              name="directorate_ar"
                              maxLength="70"
                              disabled={true}
                              errorsField={errors?.directorate_ar}
                              touched={touched?.directorate_ar}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                              className={"w-100 form-control"}
                              variant="outlined"
                            />
                          </div>
                        </Col>
                        <Col sm={6} md={6} lg={6} className="mb-4 pb-1">
                          <InputText
                            type="governorate_en"
                            controlId="governorate_en"
                            label="Governorate english"
                            value={values?.governorate_en}
                            name="governorate_en"
                            disabled={true}
                            maxLength="70"
                            errorsField={errors?.governorate_en}
                            touched={touched?.governorate_en}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            className={"w-100 form-control"}
                            variant="outlined"
                          />
                        </Col>
                        <Col sm={6} md={6} lg={6} className="mb-4 pb-1">
                          <div>
                            <InputText
                              dir="rtl"
                              type="governorate_ar"
                              controlId="governorate_ar"
                              label="Governorate arabic"
                              value={values?.governorate_ar}
                              name="governorate_ar"
                              disabled={true}
                              maxLength="70"
                              errorsField={errors?.governorate_ar}
                              touched={touched?.governorate_ar}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                              className={"w-100 form-control"}
                              variant="outlined"
                            />
                          </div>
                        </Col>
                      </Row>

                      <Stack direction="horizontal" gap={3} className="my-3">
                        <Button
                          variant="primary"
                          onClick={handleSubmit}
                          className="rounded-xs fs-15"
                          type="submit"
                          disabled={loading}
                        >
                          Update
                        </Button>
                        <Button
                          variant="outline-secondary"
                          onClick={() => {
                            navigate(-1);
                          }}
                          className="rounded-xs fs-15"
                        >
                          Cancel
                        </Button>
                      </Stack>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

export default Edit;
